// Handles inquire and coming soon overlays for collections
import { FOCUSABLE_ELEMENTS, OFF, ON } from './config/constants';
import addClass from './utils/dom/addClass';
import click from './utils/dom/click';
import removeClass from './utils/dom/removeClass';
import { lockScroll, unlockScroll } from './utils/dom/scroll';
import getImage from './utils/getImage';
import { KEY_CODES, KEY_DOWN } from './utils/keys';

// Overlay elements
const overlayBG = document.getElementById('inquire-overlay-background');
const inquireOverlay = document.getElementById('inquire-overlay');
const closeInquireOverlay = inquireOverlay?.querySelector('.hmr-overlay-close');
const notifyOverlay = document.getElementById('notify-overlay');
const closeNotifyOverlay = notifyOverlay?.querySelector('.hmr-overlay-close');
const inquireSuccessOverlay = document.getElementById('inquire-success-overlay');
const inquireSuccessBG = document.getElementById('inquire-success-overlay-background');
const successClose = document.getElementById('inquire-success-overlay-close');

let button = null;
let focusableContent = null;
let currentTab = 1;

const turnOnButton = (button) => {
  removeClass(button, OFF);
  button.removeAttribute('aria-hidden');
  button.removeAttribute('tabindex');
};

const turnOffButton = (button) => {
  addClass(button, OFF);
  button.setAttribute('aria-hidden', 'true');
  button.setAttribute('tabindex', -1);
};

const getPreview = (product, variant) => {
  const { image, material, color, size } = variant;
  const src = image.src.indexOf('https://') === 0 ? image.src : `https:${image.src}`;
  return `
  <div class="inquire-preview-img">
    ${image ? getImage({ ...image, alt: product.title, src }) : ''}
  </div>
  <div class="inquire-preview-details">
    <div>
      <strong>${product.title}</strong><br/>
      ${material ? `<div>${material}</div>` : ''}
      ${color ? `<div>Color: ${color}</div>` : ''}
      ${size ? `<div>Size: ${size}</div>` : ''}
    </div>
  </div>
  `;
};

const openInquire = (_butt, product, variant) => {
  lockScroll();
  const preview = document.getElementById('inquire-preview');
  if (preview) preview.innerHTML = getPreview(product, variant);

  // Reset success message
  const success = inquireOverlay.querySelector('.field-success');
  removeClass(success, ON);
  success.setAttribute('tabindex', -1);
  success.removeAttribute('aria-live');
  success.removeAttribute('role');

  addClass(overlayBG, ON);
  addClass(inquireOverlay, ON);
  const elements = [...inquireOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  elements.forEach((element) => element.setAttribute('tabindex', 0));
  focusableContent = elements;
  focusableContent[0]?.focus();
  const body = document.getElementById('inquiry-body');
  let text = `Product inquiry: ${product.title} ${variant.color ? variant.color : ''} ${
    variant.size ? variant.size : ''
  } `;
  if (body) body.innerText = text;

  closeInquireOverlay?.addEventListener(click(), closeOverlays);
};

const onNotifySubmit = async (event) => {
  event.preventDefault();
  const button = notifyOverlay.querySelector('button[type="submit"]');
  const innerHTML = button.innerHTML;
  const firstName = document.getElementById('notify-first-name');
  const lastName = document.getElementById('notify-last-name');
  const email = document.getElementById('notify-email');
  const phone = document.getElementById('notify-phone');
  button.setAttribute('disabled', 'disabled');
  button.innerHTML = 'Submitting...';
  const data = {
    email: email.value,
    firstName: firstName.value,
    lastName: lastName.value,
    phone: phone.value,
    consent: true,
  };
  const request = await fetch(`${window.SUBSCRIBE_SERVICE}subscribe`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
  const response = await request.json();
  if (response.ok) {
    const success = notifyOverlay.querySelector('.hmr-overlay .field-success');
    addClass(success, ON);
    success.setAttribute('tabindex', 0);
    success.setAttribute('aria-live', 'polite');
    success.setAttribute('role', 'status');
    success.focus();
  } else {
    alert('There was an error subscribing to the waitlist. Please get in touch.');
  }
  button.removeAttribute('disabled');
  button.innerHTML = innerHTML;
};

const closeOverlays = () => {
  const elements = [...inquireOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  elements.forEach((element) => element.setAttribute('tabindex', -1));
  closeInquireOverlay?.removeEventListener(click(), closeOverlays);
  const notifyElements = [...notifyOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  notifyElements.forEach((element) => element.setAttribute('tabindex', -1));
  notifyOverlay.querySelector('form')?.removeEventListener('submit', onNotifySubmit);
  closeNotifyOverlay?.removeEventListener(click(), closeOverlays);
  const success = notifyOverlay.querySelector('.hmr-overlay .field-success');
  removeClass(success, ON);
  success.setAttribute('tabindex', -1);
  success.removeAttribute('aria-live');
  success.removeAttribute('role');
  window.removeEventListener(KEY_DOWN, onKeyDown);
  overlayBG.removeEventListener(click(), closeOverlays);
  removeClass(overlayBG, ON);
  removeClass(inquireOverlay, ON);
  removeClass(notifyOverlay, ON);
  unlockScroll();
};

const openNotify = () => {
  lockScroll();
  addClass(overlayBG, ON);
  addClass(notifyOverlay, ON);
  const elements = [...notifyOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  elements.forEach((element) => element.setAttribute('tabindex', 0));
  focusableContent = elements;
  focusableContent[0]?.focus();
  notifyOverlay.querySelector('form')?.addEventListener('submit', onNotifySubmit);
  closeNotifyOverlay?.addEventListener(click(), closeOverlays);
};

const openSuccess = () => {
  lockScroll();
  // Remove contact_posted from URL if present
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('contact_posted');
  window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);

  const elements = [...inquireSuccessOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  elements.forEach((element) => element.setAttribute('tabindex', 0));

  currentTab = 1;
  focusableContent = elements;
  focusableContent[0]?.focus();

  successClose?.addEventListener(click(), closeSuccess);
  inquireSuccessBG?.addEventListener(click(), closeSuccess);
  window.addEventListener(KEY_DOWN, onSuccessKeyDown);

  addClass(inquireSuccessOverlay, ON);
  addClass(inquireSuccessBG, ON);
  document.getElementById('inquire-success-overlay-label')?.focus();
};

const closeSuccess = () => {
  const elements = [...inquireSuccessOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
  elements.forEach((element) => element.setAttribute('tabindex', -1));

  window.removeEventListener(KEY_DOWN, onSuccessKeyDown);
  successClose?.removeEventListener(click(), closeSuccess);
  inquireSuccessBG?.removeEventListener(click(), closeSuccess);
  unlockScroll();
  removeClass(inquireSuccessBG, ON);
  removeClass(inquireSuccessOverlay, ON);
};

const onSuccessKeyDown = (event) => {
  const isTabPressed = event.key === KEY_CODES.TAB;
  if (event.key === KEY_CODES.ESC) {
    closeSuccess();
    return;
  }
  if (!isTabPressed) return;
  if (focusableContent.length === 1) {
    currentTab = 0;
  } else if (event.shiftKey) {
    currentTab = currentTab - 1 < 0 ? focusableContent.length - 1 : currentTab - 1;
  } else {
    currentTab = currentTab === focusableContent.length - 1 ? 0 : currentTab + 1;
  }
  event.preventDefault();
  focusableContent[currentTab]?.focus();
};

const openOverlay = (product, variant, butt) => {
  button = butt;
  currentTab = 1;
  if (variant.comingsoon) {
    openNotify(butt, product, variant);
  } else {
    openInquire(butt, product, variant);
  }
  overlayBG.addEventListener(click(), closeOverlays);
  window.addEventListener(KEY_DOWN, onKeyDown);
};

const onKeyDown = (event) => {
  const isTabPressed = event.key === KEY_CODES.TAB;
  if (event.key === KEY_CODES.ESC) {
    closeOverlays();
    button?.focus();
    return;
  }
  if (!isTabPressed) return;
  if (event.shiftKey) {
    currentTab = currentTab - 1 < 0 ? focusableContent.length - 1 : currentTab - 1;
  } else {
    currentTab = currentTab === focusableContent.length - 1 ? 0 : currentTab + 1;
  }
  event.preventDefault();
  focusableContent[currentTab]?.focus();
};

export { closeOverlays, openOverlay, turnOnButton, turnOffButton, openSuccess };
